import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  IconComponent,
  InputComponent,
  SelectComponent,
  TooltipComponent,
} from '@purolator/ui-kit-angular';
import { TranslationService, TranslatePipe } from '../../../../core/i18n';
import { PHONE_COUNTRY_CODE_OPTIONS } from '../../constants/phone-country-codes';
import {
  blurAddressField,
  getAddressFieldErrorMessage,
  type AddressValidatedFieldKey,
} from '../../utils/address-form-errors';
import { formatPhoneNumberAsYouType } from '../../utils/phone-number-mask';
import { DisableBrowserAutofillDirective } from '../../../../shared/directives/disable-browser-autofill.directive';

/**
 * Groups country code, phone number, and extension inputs with an accessible fieldset legend.
 */
@Component({
  selector: 'app-phone-field-group',
  imports: [
    ReactiveFormsModule,
    InputComponent,
    SelectComponent,
    TooltipComponent,
    IconComponent,
    TranslatePipe,
    DisableBrowserAutofillDirective,
  ],
  templateUrl: './phone-field-group.html',
  styleUrl: './phone-field-group.scss',
})
export class PhoneFieldGroup {
  private readonly translation = inject(TranslationService);

  /** Bumped on blur so inline error bindings refresh after markAsTouched. */
  private readonly fieldInteractionVersion = signal(0);

  readonly formGroup = input.required<FormGroup>();
  readonly phoneRequired = input(true);
  readonly submitted = input(false);
  /** Prefix for AVS-safe input names (e.g. shipFrom, shipTo). */
  readonly avsNamePrefix = input.required<string>();
  /** Phone tooltip is Ship From only (OM-45 tooltip inventory). */
  readonly showPhoneTooltip = input(false);

  protected readonly countryCodeOptions = PHONE_COUNTRY_CODE_OPTIONS;

  /** Stable id for the phone info tooltip description (Ship From only). */
  protected readonly phoneTooltipDescId = computed(
    () => `address-${this.avsNamePrefix()}-phone-tooltip-desc`,
  );

  constructor() {
    effect((onCleanup) => {
      const phoneControl = this.formGroup().get('phoneNumber');

      if (!phoneControl) {
        return;
      }

      this.applyPhoneNumberMask(phoneControl.value as string, phoneControl);

      const subscription = phoneControl.valueChanges.subscribe((value) => {
        this.applyPhoneNumberMask(typeof value === 'string' ? value : '', phoneControl);
      });

      onCleanup(() => subscription.unsubscribe());
    });
  }

  /**
   * Builds a section-scoped input name so browsers do not autofill phone fields heuristically.
   */
  protected avsFieldName(field: string): string {
    return `${this.avsNamePrefix()}-${field}`;
  }

  /**
   * Returns the inline validation message for the phone number field after blur or submit.
   */
  protected phoneNumberError(): string | null {
    this.translation.revision();
    this.fieldInteractionVersion();
    return getAddressFieldErrorMessage(
      this.formGroup().get('phoneNumber'),
      'phoneNumber',
      (key, params) => this.translation.translate(key, params),
      this.submitted(),
    );
  }

  /**
   * Returns the inline validation message for the phone extension field after blur or submit.
   */
  protected phoneExtensionError(): string | null {
    this.translation.revision();
    this.fieldInteractionVersion();
    return getAddressFieldErrorMessage(
      this.formGroup().get('phoneExtension'),
      'phoneExtension',
      (key, params) => this.translation.translate(key, params),
      this.submitted(),
    );
  }

  /**
   * Marks a phone field touched on blur so frontend inline validation can display.
   */
  protected onFieldBlur(
    field: Extract<AddressValidatedFieldKey, 'phoneNumber' | 'phoneExtension'>,
  ): void {
    blurAddressField(this.formGroup().get(field));
    this.fieldInteractionVersion.update((version) => version + 1);
  }

  /**
   * Applies the North American phone mask without re-triggering value change handlers.
   */
  private applyPhoneNumberMask(
    value: string,
    phoneControl: NonNullable<ReturnType<FormGroup['get']>>,
  ): void {
    const formatted = formatPhoneNumberAsYouType(value);

    if (formatted === value) {
      return;
    }

    phoneControl.setValue(formatted, { emitEvent: false });
  }
}
